<template>
  <form @submit.prevent="verifyAndSubmit" class="form-content" role="form">
    <div class="form-content__title">
      Your feedback helps us improve our content and customer support.
    </div>
    <div class="form-content__content">
      <div class="form-content__group">
        <label class="form-content__label"
          >How was the quality of the course material?</label
        >
        <div class="form-content__star-wrapper">
          <StarRating
            :increment="1"
            :max-rating="5"
            :show-rating="false"
            :star-size="40"
            active-color="#ef7d22"
            v-model="contentRating"
          />
        </div>
        <div class="form-content__limits">
          <div class="form-content__limits--min">Poor</div>
          <div class="form-content__limits--max">Great</div>
        </div>
      </div>
      <div class="form-content__group">
        <label class="form-content__label"
          >How would you rate this instructor?</label
        >
        <div class="form-content__star-wrapper">
          <StarRating
            :increment="1"
            :max-rating="5"
            :show-rating="false"
            :star-size="40"
            active-color="#ef7d22"
            v-model="instructorRating"
          />
        </div>
        <div class="form-content__limits">
          <div class="form-content__limits--min">Poor</div>
          <div class="form-content__limits--max">Great</div>
        </div>
      </div>
      <div class="form-content__group">
        <label class="form-content__label"
          >How well did this course meet your expectations?</label
        >
        <div class="form-content__star-wrapper">
          <StarRating
            :increment="1"
            :max-rating="5"
            :show-rating="false"
            :star-size="40"
            active-color="#ef7d22"
            v-model="overallRating"
          />
        </div>
        <div class="form-content__limits">
          <div class="form-content__limits--min">Poor</div>
          <div class="form-content__limits--max">Great</div>
        </div>
      </div>
      <div class="form-content__group">
        <div class="form-content__label">Comments</div>
        <textarea
          v-model="comments"
          @focus="submissionError = false"
          class="form-content__comments"
          rows="5"
          cols="50"
        ></textarea>
      </div>
      <div class="form-content__submit-btn-wrapper">
        <button
          v-if="!isPostingData"
          type="submit"
          class="form-content__submit-btn"
        >
          Submit
          <span class="material-icons form-content__submit-btn--icon"
            >keyboard_arrow_right</span
          >
        </button>
        <ClipLoader
          v-else
          :size="1.5"
          sizeUnit="rem"
          class="form-content__loading-spinner"
        ></ClipLoader>
      </div>
    </div>
  </form>
</template>

<script>
import StarRating from 'vue-star-rating'
import { ClipLoader } from '@saeris/vue-spinners'

export default {
  name: 'FeedbackFormContent',
  components: {
    StarRating,
    ClipLoader,
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
    courseId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      submissionError: false,
      instructorRating: null,
      contentRating: null,
      overallRating: null,
      comments: '',
      isPostingData: false,
    }
  },
  methods: {
    async verifyAndSubmit() {
      if (!this.requirementsMet()) {
        this.submissionError = true
      } else {
        const feedbackSubmission = {
          userId: this.userId,
          courseId: this.courseId,
          comments:
            this.comments === '' ? 'No Comments Provided.' : this.comments,
          instructorRating: this.instructorRating,
          contentRating: this.contentRating,
          overallRating: this.overallRating,
        }
        this.isPostingData = true
        await this.$store.dispatch(
          'courses/submitCourseFeedback',
          feedbackSubmission
        )
        this.isPostingData = false
        this.$emit('feedbackSubmitted', true)
        this.$emit('closeModal', true)
        this.$store.dispatch(
          'app/setNotification',
          'Thank you for your feedback!'
        )
      }
    },
    requirementsMet() {
      const required = [
        this.contentRating,
        this.instructorRating,
        this.overallRating,
      ]
      const missing = required.filter(requirement => !requirement)
      return missing.length === 0
    },
  },
}
</script>

<style lang="scss" scoped>
.form-content {
  padding: 0 0 25px 0;
  &__title {
    font-family: 'CircularStd-Bold';
    font-size: 1.2rem;
    font-weight: 700;
    text-align: center;
    color: var(--black);
    padding: 50px 0 30px 0;
    margin: 0 0 30px 0;
    width: 100%;
    &::after {
      display: block;
      content: '';
      width: 100%;
      height: 1px;
      background: var(--black);
      margin: 15px auto 0 auto;
      @include desktop {
        width: 500px;
      }
    }
  }
  &__content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 0 50px;
  }
  &__group {
    width: 100%;
    margin: 0 auto 25px auto;
    text-align: center;
  }
  &__label {
    font-weight: 500;
    font-size: 1.1rem;
  }
  &__star-wrapper {
    margin: 10px 0 0 0;
    display: flex;
    justify-content: center;
  }
  &__limits {
    display: flex;
    justify-content: center;
    color: var(--gray);
    text-transform: uppercase;
    &--min {
      margin: 0 0 0 3px;
    }
    &--max {
      margin: 0 0 0 116px;
    }
  }
  &__submit-btn-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    margin: 0 50px 0 0;
  }
  &__submit-btn {
    $this: &;
    background: transparent;
    border-radius: 25px;
    padding: 5px 10px;
    border: 2px solid var(--black);
    color: var(--black);
    text-transform: uppercase;
    display: flex;
    align-items: center;
    cursor: pointer;
    font-weight: 700;
    &:hover {
      color: var(--white);
      background: var(--black);
      border-color: var(--orange);
    }
    &--icon {
      font-size: 1.5rem;
      justify-self: flex-end;
      margin: 0 0 0 25px;
      color: var(--white);
      background: var(--orange);
      border-radius: 25px;
    }
  }
}
</style>
